import { useMgPrisConfig } from '@retail/app/stores/app-config';
import { useAuthContext } from '@retail/auth/data-access';
import { QueryOptions } from '@shared/fetch-utils';
import { buildUrl } from '@shared/utils';
import { useMPEFetch, useMutateMPEFetch } from '@retail/app/hooks';
import { CalculusArea, ConditionsNewDTO, DraftConditionDTO } from '@retail/calculus/types';
import { mpeApi } from '@retail/mgpris/config';
import { HierarchyLevels } from '@retail/products/types';

interface ConditionParams extends QueryOptions {
  assortmentId: number;
  supplierNumber?: number;
  productGroupNumber?: string;
  discountGroupCode?: string;
  supplierItemId?: number;
  conditionType?: CalculusArea;
  bindingType?: HierarchyLevels;
  conditionState: string;
  assortmentType: string;
}

export function useFetchConditions({
  assortmentId,
  supplierNumber,
  productGroupNumber,
  discountGroupCode,
  supplierItemId,
  conditionType,
  bindingType,
  conditionState,
  assortmentType,
  ...queryOptions
}: ConditionParams) {
  const { mpe } = useMgPrisConfig();
  const { token } = useAuthContext();

  return useMPEFetch<ConditionsNewDTO[]>({
    method: 'GET',
    queryKey: [
      'conditions',
      assortmentId,
      supplierNumber,
      productGroupNumber,
      discountGroupCode,
      supplierItemId,
      conditionType,
      bindingType,
      conditionState,
      assortmentType,
    ],
    token,
    url: buildUrl({
      base: mpe.url,
      route: mpeApi.conditions.getFullLinkPath(),
      searchParams: {
        assortmentId,
        supplierNumber,
        productGroupNumber,
        discountGroupCode,
        supplierItemId,
        conditionType,
        bindingType,
        conditionState,
        assortmentType,
      },
    }),
    ...queryOptions,
  });
}

interface DraftConditionParams extends QueryOptions {
  assortmentId: number;
}

export function useFetchDraftConditions({ assortmentId, ...queryOptions }: DraftConditionParams) {
  const { mpe } = useMgPrisConfig();
  const { token } = useAuthContext();

  return useMPEFetch<DraftConditionDTO[]>({
    method: 'GET',
    queryKey: ['draft-conditions', assortmentId],
    token,
    url: buildUrl({
      base: mpe.url,
      route: mpeApi.draftConditions.getFullLinkPath(),
      searchParams: { assortmentId },
    }),
    ...queryOptions,
  });
}

export const useMutateActivateDraftedConditions = ({
  assortmentId,
  ...queryOptions
}: DraftConditionParams) => {
  const { mpe } = useMgPrisConfig();
  const { token } = useAuthContext();

  return useMutateMPEFetch<void, number[]>({
    url: buildUrl({
      base: mpe.url,
      route: mpeApi.activateConditions.getFullLinkPath(),
      searchParams: { baseAssortmentId: assortmentId },
    }),

    method: 'POST',
    token,
    ...queryOptions,
  });
};
